import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { PayingForCareCoverageModal } from "./PayingForCareModal"
import { FormatNumberOptions, FormattedMessage, useIntl } from "react-intl"
import { getCategoryName } from "../ReimbursementCategoryMessages"
import { getCurrencyCode } from "#/redux/reducers/companyInfo"
import { PlanInfoBadgeContainer } from "../../../containers/planinfo/PlanInfoBadgeContainer"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { getCategoryCoverage } from "../../../../utils/CategoryCoverage"
import { Subsidy } from "#/lib/carrot-api/types/Subsidy"

export interface FormattedSubsidy extends Subsidy {
  categoryName: string
  formattedYearlyBenefit: string
  formattedLifetimeBenefit: string
}

const SinglePlanDisplay = ({ subsidy, currencyCode }: { subsidy: Subsidy; currencyCode: string }): JSX.Element => {
  const intl = useIntl()
  const hasUnlimitedBenefit = useMemo(() => subsidy?.hasUnlimitedBenefit, [subsidy?.hasUnlimitedBenefit])
  const yearlyBenefit = useMemo(() => subsidy?.yearlyAmounts.max, [subsidy?.yearlyAmounts.max])
  const lifetimeMax = useMemo(() => subsidy?.lifetimeAmounts.max, [subsidy?.lifetimeAmounts.max])
  const reimbursementPercent: number | null = useMemo(() => {
    if (subsidy?.reimbursementRules?.length === 1) {
      return subsidy.reimbursementRules[0].reimbursementPercent
    }
    return null
  }, [subsidy?.reimbursementRules])
  const yearlyBenefitWithoutCents = yearlyBenefit / 100
  const lifetimeMaxWithoutCents = lifetimeMax / 100
  const formattedYearlyBenefit = intl.formatNumber(yearlyBenefitWithoutCents, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
  const formattedLifetimeMax = intl.formatNumber(lifetimeMaxWithoutCents, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

  if (hasUnlimitedBenefit) {
    return (
      <Box
        sx={(theme) => ({
          marginInlineStart: theme.tokens.spacing.xl,
          marginInlineEnd: theme.tokens.spacing.xl,
          marginBlockStart: theme.tokens.spacing.md,
          marginBlockEnd: theme.tokens.spacing.md
        })}
      >
        <FormattedMessage defaultMessage="Unlimited eligible expenses" />
      </Box>
    )
  } else {
    return (
      <>
        {reimbursementPercent !== null && (
          <>
            <Box
              sx={(theme) => ({
                marginInlineStart: theme.tokens.spacing.xl,
                marginInlineEnd: theme.tokens.spacing.xl,
                marginBlockStart: theme.tokens.spacing.md,
                marginBlockEnd: theme.tokens.spacing.md
              })}
            >
              <FormattedMessage
                defaultMessage="{reimbursementPercentage, number, ::percent} of eligible expenses"
                values={{ reimbursementPercentage: reimbursementPercent / 100 }}
              />
            </Box>
          </>
        )}
        {yearlyBenefit !== lifetimeMax && (
          <Box
            sx={(theme) => ({
              marginInlineStart: theme.tokens.spacing.xl,
              marginInlineEnd: theme.tokens.spacing.xl,
              marginBlockStart: theme.tokens.spacing.md,
              marginBlockEnd: theme.tokens.spacing.md
            })}
          >
            <FormattedMessage
              defaultMessage="{formattedYearlyBenefit} per benefit year"
              values={{ formattedYearlyBenefit }}
            />
          </Box>
        )}
        {lifetimeMax && (
          <Box
            sx={(theme) => ({
              marginInlineStart: theme.tokens.spacing.xl,
              marginInlineEnd: theme.tokens.spacing.xl,
              marginBlockStart: theme.tokens.spacing.md,
              marginBlockEnd: theme.tokens.spacing.md
            })}
          >
            <FormattedMessage
              defaultMessage="{formattedLifetimeMax} lifetime maximum"
              values={{ formattedLifetimeMax }}
            />
          </Box>
        )}
        <PlanInfoBadgeContainer
          subsidy={subsidy}
          allSubsidies={[subsidy]}
          sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.xs })}
        />
      </>
    )
  }
}
function filterOutOtherSubsidies(subsidies: Subsidy[]) {
  return subsidies.filter(
    (subsidy: Subsidy) =>
      !!subsidy.reimbursementCategoryNameMessageKey &&
      (subsidy.reimbursementCategoryNameMessageKey.toString() !== "OTHER" || subsidy.lifetimeAmounts.max !== 0)
  )
}

const useFormatSubsidies = (
  subsidies: Subsidy[],
  hasQmeCategory: boolean,
  currencyCode: string
): FormattedSubsidy[] => {
  const intl = useIntl()
  return subsidies.map((subsidy) => {
    const categoryName = intl.formatMessage(
      getCategoryName(subsidy.reimbursementCategoryNameMessageKey.toString(), hasQmeCategory)
    )
    const benefitFormatOptions: FormatNumberOptions = {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }
    const yearlyBenefitWithoutCents = subsidy.hasUnlimitedBenefit ? null : subsidy.yearlyAmounts.max / 100

    const lifetimeBenefitWithoutCents =
      subsidy.hasUnlimitedBenefit || subsidy.lifetimeAmounts.max === null ? null : subsidy.lifetimeAmounts.max / 100

    const formattedYearlyBenefit =
      yearlyBenefitWithoutCents !== null
        ? intl.formatNumber(yearlyBenefitWithoutCents, benefitFormatOptions)
        : intl.formatMessage({ defaultMessage: "Unlimited" })

    const formattedLifetimeBenefit =
      lifetimeBenefitWithoutCents !== null
        ? intl.formatNumber(lifetimeBenefitWithoutCents, benefitFormatOptions)
        : intl.formatMessage({ defaultMessage: "Unlimited" })

    return {
      ...subsidy,
      categoryName,
      formattedYearlyBenefit,
      formattedLifetimeBenefit
    }
  })
}

const SubsidyDisplay = ({
  subsidy,
  hasQmeCategory,
  hasMedicationsCategory,
  subsidies,
  lifetimeMaxOnly
}: {
  subsidy: FormattedSubsidy
  hasQmeCategory: boolean
  hasMedicationsCategory: boolean
  subsidies: Subsidy[]
  lifetimeMaxOnly: boolean
}): JSX.Element => {
  return (
    <Box key={subsidy.categoryName + "multiple-plan-display"}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          inlineSize: "100%",
          marginBlockStart: theme.tokens.spacing.md,
          marginBlockEnd: theme.tokens.spacing.md
        })}
      >
        <Box sx={{ display: "flex", flexDirection: "column", inlineSize: "50%" }}>
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "center",
              flex: "1 1 50%",
              [theme.breakpoints.down("md")]: {
                flexShrink: 3
              }
            })}
          >
            <Box
              component="img"
              src={
                subsidy.reimbursementCategoryIconName
                  ? `/images/${subsidy.reimbursementCategoryIconName}.png`
                  : `/images/icn-expenses-other.png`
              }
              height="2.25rem"
              width="2.25rem"
              alt=""
              sx={(theme) => ({
                [theme.breakpoints.down("md")]: {
                  display: "none"
                }
              })}
            />
            <Typography
              sx={(theme) => ({
                [theme.breakpoints.up("md")]: {
                  marginInlineStart: (theme) => theme.tokens.spacing.sm
                }
              })}
            >
              {subsidy.categoryName}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "space-evenly",
            alignItems: "self-start",
            flex: "1 1 50%",
            [theme.breakpoints.down("md")]: {
              flexShrink: 1
            },
            gap: (theme) => theme.tokens.spacing.xs
          })}
        >
          {lifetimeMaxOnly && (
            <Typography variant="h6" color="text.primary">
              {subsidy.formattedYearlyBenefit}
            </Typography>
          )}
          <Typography variant="h6">{subsidy.formattedLifetimeBenefit}</Typography>
        </Box>
      </Box>
      <Box>
        <Box
          sx={(theme) => ({
            paddingInlineStart: (theme) => theme.tokens.spacing.xl,
            marginInlineStart: (theme) => theme.tokens.spacing.md,
            [theme.breakpoints.down("md")]: {
              marginInlineStart: 0,
              paddingInlineStart: 0
            }
          })}
        >
          <PayingForCareCoverageModal
            subsidyCategory={subsidy}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
          <Box sx={{ marginBlockStart: (theme) => theme.spacing(theme.tokens.spacing.sm) }}>
            <PlanInfoBadgeContainer subsidy={subsidy} allSubsidies={subsidies} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const MultiplePlanDisplay = ({
  subsidies,
  lifetimeMaxOnly,
  currencyCode
}: {
  subsidies: Subsidy[]
  lifetimeMaxOnly: boolean
  currencyCode: string
}): JSX.Element => {
  const coverage = getCategoryCoverage(subsidies)
  const hasQmeCategory = coverage.qme
  const hasMedicationsCategory = coverage.medication

  const filteredSubsidies: Subsidy[] = filterOutOtherSubsidies(subsidies)
  const formattedSubsidies: FormattedSubsidy[] = useFormatSubsidies(filteredSubsidies, hasQmeCategory, currencyCode)

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-evenly",
          inlineSize: "50%",
          marginInlineStart: "50%"
        }}
      >
        {lifetimeMaxOnly && (
          <Typography variant="h6" color={(theme) => theme.palette.text.tertiary}>
            <FormattedMessage defaultMessage="Annual Max" />
          </Typography>
        )}
        <Typography variant="h6" color={(theme) => theme.palette.text.tertiary}>
          <FormattedMessage defaultMessage="Lifetime Max" />
        </Typography>
      </Box>
      {formattedSubsidies.map((subsidy) => (
        <SubsidyDisplay
          key={subsidy.categoryName}
          subsidy={subsidy}
          hasQmeCategory={hasQmeCategory}
          hasMedicationsCategory={hasMedicationsCategory}
          subsidies={subsidies}
          lifetimeMaxOnly={lifetimeMaxOnly}
        />
      ))}
    </>
  )
}

const PayingForCareCoverageDetails = (): JSX.Element => {
  const subsidies: Subsidy[] = useSelector(getSubsidies)
  const currencyCode = useSelector(getCurrencyCode)
  const hasMultiplePlans = useMemo(() => subsidies?.length > 1, [subsidies])
  const lifetimeMaxOnly = useMemo(
    () => subsidies?.some((plan) => plan.yearlyAmounts.max !== plan.lifetimeAmounts.max),
    [subsidies]
  )

  return (
    <>
      {hasMultiplePlans ? (
        <MultiplePlanDisplay subsidies={subsidies} lifetimeMaxOnly={lifetimeMaxOnly} currencyCode={currencyCode} />
      ) : (
        <SinglePlanDisplay subsidy={subsidies?.[0]} currencyCode={currencyCode} />
      )}
    </>
  )
}

export { PayingForCareCoverageDetails }
